import { useCallback } from 'react';
import { MaterialDto, OrderLineItemDto, OrderReviewLineItemDto, QueryResultsDto } from '../dtos';
import { LineItemTypes } from './constants';

export interface MaterialDuplicateCheckerInput {
    lineItems: (OrderLineItemDto | OrderReviewLineItemDto)[];
    materialsData?: QueryResultsDto<MaterialDto>;
}

/**
 * Reusable hook for checking duplicate materials and getting excluded material IDs
 * for hauling line items. Works with both OrderLineItemDto and OrderReviewLineItemDto.
 */
export function useMaterialDuplicateChecker({ lineItems, materialsData }: MaterialDuplicateCheckerInput) {
    /**
     * Gets the material ID from a material line item, with fallback to lookup by code
     */
    const getMaterialIdFromLineItem = useCallback(
        (materialLineItem: any): string | undefined => {
            // Try to get materialId from the lineItem first
            let materialId = materialLineItem.materialId || materialLineItem.material?.id;

            // If materialId is not available, look it up by material code (description)
            if (!materialId && materialLineItem.description && materialsData?.pageResults) {
                const material = materialsData.pageResults.find(
                    (m) => m.code === materialLineItem.description
                );
                materialId = material?.id;
            }

            return materialId;
        },
        [materialsData]
    );

    /**
     * Gets the nested LineItemDto from an OrderLineItemDto or OrderReviewLineItemDto
     */
    const getNestedLineItem = useCallback(
        (lineItem: OrderLineItemDto | OrderReviewLineItemDto): any => {
            return 'lineItem' in lineItem ? lineItem.lineItem : undefined;
        },
        []
    );

    /**
     * Gets the quote line item ID for a hauling line item.
     * The haulingLineItemId could be:
     * 1. A quote line item ID (when selecting from quote price list)
     * 2. An order/order review line item's lineItemId (if the hauling line item is already in the order/order review)
     */
    const getQuoteLineItemIdForHaulingLineItem = useCallback(
        (haulingLineItemId: string): string => {
            // First, find the hauling line item in the order/order review if it exists
            const haulingLineItem = lineItems.find(
                (lineItem) => {
                    const item = getNestedLineItem(lineItem);
                    return (
                        item?.type === LineItemTypes.SiteHauling &&
                        (('lineItemId' in lineItem ? lineItem.lineItemId : undefined) === haulingLineItemId ||
                            item?.id === haulingLineItemId)
                    );
                }
            );

            // Get the actual quote line item ID to compare against
            const quoteLineItemId =
                haulingLineItem && 'lineItemId' in haulingLineItem
                    ? haulingLineItem.lineItemId
                    : haulingLineItemId;

            return quoteLineItemId ?? haulingLineItemId;
        },
        [lineItems, getNestedLineItem]
    );

    /**
     * Checks if a material is already used for a specific hauling line item
     */
    const checkForDuplicateMaterial = useCallback(
        (materialId: string, haulingLineItemId: string): boolean => {
            const quoteLineItemId = getQuoteLineItemIdForHaulingLineItem(haulingLineItemId);

            const isDuplicate = lineItems.some((lineItem) => {
                const item = getNestedLineItem(lineItem);

                // Check if it's a material line item
                if (item?.type !== LineItemTypes.Material) {
                    return false;
                }

                // Check if materialId matches and associatedLineItemId matches the quote line item ID
                const materialLineItem = item as any;
                const existingMaterialId = getMaterialIdFromLineItem(materialLineItem);

                return (
                    existingMaterialId === materialId &&
                    materialLineItem.associatedLineItemId === quoteLineItemId
                );
            });

            return isDuplicate;
        },
        [lineItems, getQuoteLineItemIdForHaulingLineItem, getMaterialIdFromLineItem, getNestedLineItem]
    );

    /**
     * Gets all material IDs that are already used for a specific hauling line item
     */
    const getExcludedMaterialIdsForHaulingLineItem = useCallback(
        (haulingLineItemId: string): string[] => {
            const quoteLineItemId = getQuoteLineItemIdForHaulingLineItem(haulingLineItemId);

            const excludedMaterialIds = lineItems
                .filter((lineItem) => {
                    const item = getNestedLineItem(lineItem);

                    // Check if it's a material line item
                    if (item?.type !== LineItemTypes.Material) {
                        return false;
                    }

                    // Check if associatedLineItemId matches the quote line item ID
                    const materialLineItem = item as any;
                    return materialLineItem.associatedLineItemId === quoteLineItemId;
                })
                .map((lineItem) => {
                    const item = getNestedLineItem(lineItem);
                    const materialLineItem = item as any;
                    return getMaterialIdFromLineItem(materialLineItem);
                })
                .filter((materialId): materialId is string => materialId !== undefined && materialId !== null && materialId !== '');

            return excludedMaterialIds;
        },
        [lineItems, getQuoteLineItemIdForHaulingLineItem, getMaterialIdFromLineItem, getNestedLineItem]
    );

    return {
        checkForDuplicateMaterial,
        getExcludedMaterialIdsForHaulingLineItem,
    };
}

